/**
 * Arithmetic behind the usage panel: cohort → time range, series → totals /
 * chart points, limit → remaining ratio.
 *
 * Cohorts are the cost dashboard's `TIME_COHORTS` ids (today / week / month /
 * all) but NOT its windows: the dashboard's week/month are the PREVIOUS full
 * calendar week/month; ours are the CURRENT ones, from their start up to now
 * (calendar weeks start on Monday). All UTC — the hub's ledger windows are UTC
 * days/weeks/months, so a local-time cohort would straddle two ledger buckets.
 *
 * **Pure.** `now` is a parameter everywhere, so tests pin the clock.
 */
import type { LLMChainHop, LLMChainRemaining, LLMUsageCounters, LLMUsageGranularity, LLMUsagePoint } from '@sdk';
import { formatValue, type TimeCohort } from '@src/components/cost-dashboard/constants';

import { endpointIdFromTypeId } from './llm-endpoints-pointer';

export interface UsageRange {
  /** Epoch seconds, inclusive. */
  from: number;
  /** Epoch seconds, exclusive. */
  to: number;
  granularity: LLMUsageGranularity;
}

/** The ledger began with the feature; "all time" needs a finite start. */
const ALL_TIME_DAYS = 365;

/** [from, to) for a cohort, in epoch seconds, UTC-aligned. `to` is `now`
 *  floored to the minute (+1 so the current minute's start is inside), so
 *  callers computing it independently within a minute get one query key. */
export function cohortRange(cohort: TimeCohort, now: Date = new Date()): UsageRange {
  const dayStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const to = Math.floor(now.getTime() / 60_000) * 60 + 1;
  switch (cohort) {
    case 'today':
      return { from: Math.floor(dayStart / 1000), to, granularity: 'hour' };
    case 'thisWeek': {
      const daysSinceMonday = (now.getUTCDay() + 6) % 7;
      return { from: Math.floor(dayStart / 1000) - daysSinceMonday * 86400, to, granularity: 'day' };
    }
    case 'thisMonth': {
      const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
      return { from: Math.floor(monthStart / 1000), to, granularity: 'day' };
    }
    case 'allTime':
    default:
      return { from: Math.floor(dayStart / 1000) - ALL_TIME_DAYS * 86400, to, granularity: 'day' };
  }
}

export const ZERO_COUNTERS: LLMUsageCounters = {
  requests: 0,
  fallbacks: 0,
  errors: 0,
  input_tokens: 0,
  output_tokens: 0,
  cache_read_tokens: 0,
  cache_write_tokens: 0,
  cost_usd: 0,
  latency_ms_sum: 0,
  ttfb_ms_sum: 0,
  estimated_requests: 0,
  unpriced_requests: 0,
  total_tokens: 0,
};

const COUNTER_KEYS = Object.keys(ZERO_COUNTERS) as (keyof LLMUsageCounters)[];

export function addCounters(a: LLMUsageCounters, b: Partial<LLMUsageCounters>): LLMUsageCounters {
  const out = { ...a };
  for (const k of COUNTER_KEYS) out[k] = (a[k] ?? 0) + (b[k] ?? 0);
  return out;
}

export interface ChartPoint {
  /** Epoch seconds. */
  t: number;
  /** Axis label. */
  label: string;
  cost_usd: number;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  requests: number;
  errors: number;
  fallbacks: number;
}

function labelFor(bucketStart: number, granularity: LLMUsageGranularity): string {
  const d = new Date(bucketStart * 1000);
  const two = (n: number) => String(n).padStart(2, '0');
  return granularity === 'hour' ? `${two(d.getUTCHours())}:00` : `${two(d.getUTCMonth() + 1)}-${two(d.getUTCDate())}`;
}

/**
 * Series → one point per bucket, dims collapsed, sorted by time, with EMPTY
 * buckets filled across `range` so a quiet hour shows as zero rather than a
 * gap the line skips over.
 */
export function toChartPoints(series: readonly LLMUsagePoint[], range: UsageRange): ChartPoint[] {
  const { granularity } = range;
  const step = granularity === 'hour' ? 3600 : 86400;
  const byBucket = new Map<number, LLMUsageCounters>();
  for (const p of series) {
    const bucket = Math.floor(p.bucket_start / step) * step;
    byBucket.set(bucket, addCounters(byBucket.get(bucket) ?? ZERO_COUNTERS, p));
  }
  const first = Math.floor(range.from / step) * step;
  const last = Math.floor(Math.max(range.to - 1, range.from) / step) * step;
  const out: ChartPoint[] = [];
  for (let t = first; t <= last; t += step) {
    const c = byBucket.get(t) ?? ZERO_COUNTERS;
    out.push({
      t,
      label: labelFor(t, granularity),
      cost_usd: c.cost_usd,
      total_tokens: c.total_tokens,
      input_tokens: c.input_tokens,
      output_tokens: c.output_tokens,
      requests: c.requests,
      errors: c.errors,
      fallbacks: c.fallbacks,
    });
  }
  return out;
}

/** 0..1 share of a limit that is left; 1 when there is no limit. Clamped. */
export function remainingRatio(r: Pick<LLMChainRemaining, 'limit' | 'remaining'> | null | undefined): number {
  if (!r || !(r.limit > 0)) return 1;
  return Math.min(1, Math.max(0, r.remaining / r.limit));
}

/** Round to the cents/millicents the dashboard shows. */
export function formatUsd(value: number): string {
  if (!value) return '$0';
  // A single haiku call is ~$0.00003: show enough digits that a real spend never reads as $0.
  if (value < 0.0001) return `$${value.toPrecision(2)}`;
  if (value < 0.01) return `$${value.toFixed(5)}`;
  if (value < 1) return `$${value.toFixed(3)}`;
  return `$${value.toFixed(2)}`;
}

/** How full a limit is: 0 when nothing is used, 1 when it is spent; 0 when
 *  there is no limit. The mirror of {@link remainingRatio} — one arithmetic,
 *  so a bar and a headline can never disagree. */
export function usedRatio(r: Pick<LLMChainRemaining, 'limit' | 'remaining'> | null | undefined): number {
  return 1 - remainingRatio(r);
}

export type RatioTone = 'ok' | 'amber' | 'destructive';

/** Tone of a fill: shifts at 70 % and 90 % used (30 % / 10 % left). The ONE
 *  place the thresholds live, so moving one moves every bar, headline and
 *  badge at once. */
export function ratioTone(used: number): RatioTone {
  if (used >= 0.9) return 'destructive';
  if (used >= 0.7) return 'amber';
  return 'ok';
}

export function isCostKey(key: string): boolean {
  return key.startsWith('cost_usd');
}

/** One cost limit's remaining entry, with WHICH limit it is (`cost_usd_total`...). The hub's
 *  `remaining` map is keyed by limit; picking one entry out of it must keep the key, or a bar
 *  cannot label or format it. */
export interface CostLeft {
  key: string;
  remaining: LLMChainRemaining;
}

/** Cost left per hub endpoint typeid; `null` until read, or when the chain has no cost cap. */
export type CostLeftByEndpoint = Record<string, CostLeft | null>;

/** The tighter of two cost limits (the smaller `remaining`); `null` only when both are. */
export function tighterCost(a: CostLeft | null | undefined, b: CostLeft | null | undefined): CostLeft | null {
  if (!a || !b) return a ?? b ?? null;
  return b.remaining.remaining < a.remaining.remaining ? b : a;
}

/**
 * The tightest COST cap along a chain: the smallest `remaining` over every hop's cost limits.
 * Token caps are ignored on purpose — a "left" figure is money, and mixing units in one number
 * is how a budget reads as $0 when it is 50k tokens.
 */
export function tightestCostLimit(hops: readonly LLMChainHop[] | undefined): CostLeft | null {
  let best: CostLeft | null = null;
  for (const hop of hops ?? []) {
    for (const [key, remaining] of Object.entries(hop.remaining ?? {})) {
      if (isCostKey(key) && remaining.limit > 0) best = tighterCost(best, { key, remaining });
    }
  }
  return best;
}

/** Dollars left on a cost limit, never negative; `undefined` when there is no limit to speak of. */
export function usdLeft(left: CostLeft | null | undefined): number | undefined {
  return left ? Math.max(0, left.remaining.remaining) : undefined;
}

/** Format an amount in its limit key's own unit: dollars, tokens, plain count. */
export function formatAmount(key: string, value: number): string {
  if (isCostKey(key)) return formatUsd(value);
  if (key.startsWith('tokens')) return formatValue(value, 'tokens');
  return String(value);
}

/** Display name for a `by=child` usage dimension: the hub's `names` map wins
 *  (it covers children the caller may not list), then a local lookup by bare
 *  id, then the raw dimension. */
export function childLabel(
  dim: string,
  names?: Record<string, string>,
  lookup?: (id: string) => string | undefined,
): string {
  return names?.[dim] ?? lookup?.(endpointIdFromTypeId(dim)) ?? dim;
}

/** The green every cost chart in the hub draws with. */
export const COST_CHART_COLOR = '#22c55e';
